import { useEffect, useRef, useState } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import {
  Shield, FileText, CheckCircle2, AlertTriangle, Loader2, Clock, Download, RefreshCw, Upload, X, Smartphone, Eye, Pencil, Save
} from 'lucide-react';
import { useConfirm } from './ui';

interface DpaTemplate {
  id: string;
  dpa_type: string;
  version: string;
  status: string;
  is_draft_marker: boolean;
  effective_from: string | null;
  subprocessor_snapshot: any[];
  notes: string;
  template_name: string;
  published_at: string | null;
  published_by_name: string | null;
  published_by_email: string | null;
}

interface Signature {
  id: string;
  dpa_type: string;
  party_type: string;
  party_name: string;
  template_version: string;
  signed_at: string;
  signed_by_name: string;
  signed_by_email: string;
  brreg_verification_status: string;
  eseal_applied: boolean;
  signed_under_draft: boolean;
  status: string;
}

interface GracePeriod {
  party_type: string;
  party_name: string;
  dpa_type: string;
  grace_period_ends_at: string;
  resolved_at: string | null;
  locked_at: string | null;
}

const DPA_LABELS: Record<string, string> = {
  kompetanseforbundet_studieforbund: 'DPA-1 — Studieforbund',
  kompetanseforbundet_medlem: 'DPA-2 — Medlemsorganisasjon',
  lms_addendum: 'DPA-3 — LMS-tillegg'
};

async function fetchDpaPdf(signatureId: string): Promise<void> {
  const res = await authenticatedFetch(`/api/dpa/pdf/${signatureId}`);
  if (!res.ok) throw new Error(`Kunne ikke hente PDF: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

interface Discrepancy {
  id: string;
  party_type: string;
  party_name: string;
  field_name: string;
  local_value: string | null;
  brreg_value: string;
  severity: 'info' | 'warning' | 'critical';
  detected_at: string;
  detected_from: string;
  resolved_at: string | null;
  resolution: string | null;
  resolution_notes: string | null;
  resolved_by_name: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  organization_name: 'Organisasjonsnavn',
  organization_number: 'Organisasjonsnummer',
  address: 'Adresse'
};

const RESOLUTION_LABELS: Record<string, string> = {
  updated_from_brreg: 'Oppdatert fra Brreg',
  kept_local: 'Beholdt lokal (Brreg utdatert)',
  contacted_org: 'Kontaktet organisasjonen',
  ignored: 'Ignorert'
};

export default function DpaAdminManagement() {
  const { confirm } = useConfirm();
  const [templates, setTemplates] = useState<DpaTemplate[]>([]);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [gracePeriods, setGracePeriods] = useState<GracePeriod[]>([]);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'templates' | 'signatures' | 'grace' | 'discrepancies'>('templates');
  const [publishTarget, setPublishTarget] = useState<{ dpa_type: string; version: string; label: string } | null>(null);
  const [contentTarget, setContentTarget] = useState<{ id: string; label: string; canEdit: boolean } | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  async function handleDownloadPdf(signatureId: string) {
    if (downloadingIds.has(signatureId)) return;
    setDownloadingIds(prev => new Set(prev).add(signatureId));
    try {
      await fetchDpaPdf(signatureId);
    } catch (e: any) {
      alert('Kunne ikke laste ned PDF: ' + e.message);
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(signatureId);
        return next;
      });
    }
  }

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tRes, sRes, gRes, dRes] = await Promise.all([
        authenticatedFetch('/api/dpa/templates'),
        authenticatedFetch('/api/dpa/admin/signatures'),
        authenticatedFetch('/api/dpa/admin/grace-periods'),
        authenticatedFetch('/api/dpa/admin/discrepancies')
      ]);
      const tData = await tRes.json();
      if (tRes.ok) setTemplates(tData.templates || []);
      const sData = await sRes.json();
      if (sRes.ok) setSignatures(sData.signatures || []);
      const gData = await gRes.json();
      if (gRes.ok) setGracePeriods(gData.grace_periods || []);
      const dData = await dRes.json();
      if (dRes.ok) setDiscrepancies(dData.discrepancies || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resolveDiscrepancy = async (id: string, resolution: string, notes?: string) => {
    const ok = await confirm({
      title: 'Bekreft løsning',
      message: `Vil du markere avvik som: "${RESOLUTION_LABELS[resolution]}"?`,
      variant: 'info',
      confirmLabel: 'Bekreft',
    });
    if (!ok) return;
    try {
      const res = await authenticatedFetch(`/api/dpa/admin/discrepancies/${id}/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution, notes })
      });
      if (!res.ok) {
        const d = await res.json();
        alert('Feil: ' + (d.error || res.statusText));
        return;
      }
      loadAll();
    } catch (e: any) {
      alert('Feil: ' + e.message);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const now = Date.now();

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-kf-primary flex items-center gap-2">
            <Shield className="h-6 w-6" /> Databehandleravtaler (DPA)
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Oversikt over aktive avtaler, signeringer og grace-perioder for eksisterende parter
          </p>
        </div>
        <button onClick={loadAll} className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2 text-sm">
          <RefreshCw className="h-4 w-4" /> Oppdater
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 inline mr-2" />{error}
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {(['templates', 'signatures', 'grace', 'discrepancies'] as const).map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
                    className={`px-4 py-2 border-b-2 text-sm font-medium ${activeTab === t ? 'border-kf-primary text-kf-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t === 'templates' && `Avtaler (${templates.length})`}
              {t === 'signatures' && `Signaturer (${signatures.length})`}
              {t === 'grace' && `Grace-perioder (${gracePeriods.filter(g => !g.resolved_at).length})`}
              {t === 'discrepancies' && (
                <span className="flex items-center gap-1">
                  Brreg-avvik ({discrepancies.filter(d => !d.resolved_at).length})
                  {discrepancies.filter(d => !d.resolved_at && d.severity === 'critical').length > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">!</span>
                  )}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-kf-primary mx-auto" />
        </div>
      ) : (
        <>
          {activeTab === 'templates' && (
            <div className="space-y-3">
              <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-3 text-sm text-kf-blue-900">
                <strong>Om avtalene:</strong> Hver avtale publiseres av Kompetanseforbundet som databehandler.
                Publiserte avtaler er bindende og kan signeres av motparter (studieforbund/medlem). Ikke-publiserte avtaler er utkast under forberedelse.
              </div>
              {templates.length === 0 && <div className="text-sm text-gray-500 py-8 text-center">Ingen aktive avtaler</div>}
              {templates.map(t => (
                <div key={t.id} className={`bg-white border rounded-lg p-4 ${t.published_at ? 'border-green-200' : 'border-yellow-300'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="h-5 w-5 text-kf-primary" />
                        <h3 className="font-semibold text-gray-900">{DPA_LABELS[t.dpa_type] || t.dpa_type}</h3>
                        {t.is_draft_marker && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">UTKAST</span>
                        )}
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">v{t.version}</span>
                        {t.published_at ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Publisert
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">Ikke publisert</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{t.template_name}</p>
                      {t.published_at && (
                        <div className="mt-2 text-xs text-green-800">
                          Publisert {new Date(t.published_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', dateStyle: 'short', timeStyle: 'short' })}
                          {' av '}<strong>Kompetanseforbundet</strong>
                        </div>
                      )}
                      {t.notes && <p className="text-xs text-gray-500 mt-2 italic">{t.notes}</p>}
                      {t.subprocessor_snapshot && t.subprocessor_snapshot.length > 0 && (
                        <details className="mt-3 text-xs">
                          <summary className="cursor-pointer text-gray-600">Underdatabehandlere ({t.subprocessor_snapshot.length})</summary>
                          <ul className="mt-2 space-y-1 pl-4">
                            {t.subprocessor_snapshot.map((sp: any, i: number) => (
                              <li key={i}>• <strong>{sp.name}</strong> ({sp.country}) — {sp.purpose}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button
                        onClick={() => setContentTarget({ id: t.id, label: `${DPA_LABELS[t.dpa_type] || t.dpa_type} v${t.version}`, canEdit: false })}
                        className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm flex items-center gap-2">
                        <Eye className="h-4 w-4" /> Vis innhold
                      </button>
                      {!t.published_at && (
                        <>
                          <button
                            onClick={() => setContentTarget({ id: t.id, label: `${DPA_LABELS[t.dpa_type] || t.dpa_type} v${t.version}`, canEdit: true })}
                            className="px-3 py-2 border border-kf-blue-300 text-kf-blue-700 rounded hover:bg-kf-blue-50 text-sm flex items-center gap-2">
                            <Pencil className="h-4 w-4" /> Rediger utkast
                          </button>
                          <button
                            onClick={() => setPublishTarget({ dpa_type: t.dpa_type, version: t.version, label: DPA_LABELS[t.dpa_type] || t.dpa_type })}
                            className="px-3 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary/90 text-sm flex items-center gap-2">
                            <Upload className="h-4 w-4" /> Publiser
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'signatures' && (
            <div className="space-y-2">
              {signatures.length === 0 && <div className="text-sm text-gray-500 py-8 text-center">Ingen signaturer registrert</div>}
              {signatures.map(s => (
                <div key={s.id} className="bg-white border border-gray-200 rounded p-3 flex items-center justify-between text-sm">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {s.status === 'active' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> :
                       <span className="text-gray-400">•</span>}
                      <span className="font-medium text-gray-900">{s.party_name}</span>
                      <span className="text-xs text-gray-500">{DPA_LABELS[s.dpa_type] || s.dpa_type}</span>
                      {/* UTKAST-badge vises kun på aktive signaturer — på superseded
                          er informasjonen overflødig (de er uansett erstattet). */}
                      {s.signed_under_draft && s.status === 'active' && (
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">UTKAST</span>
                      )}
                      {s.status === 'superseded' && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">superseded</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      Signert av <strong>{s.signed_by_name}</strong> ({s.signed_by_email})
                      · {new Date(s.signed_at).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', dateStyle: 'short', timeStyle: 'short' })}
                      · v{s.template_version}
                      · Brreg: <span className={s.brreg_verification_status.startsWith('verified') ? 'text-green-700' : 'text-red-700'}>{s.brreg_verification_status}</span>
                      · eSeal: {s.eseal_applied ? '✓' : '✗'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadPdf(s.id)}
                    disabled={downloadingIds.has(s.id)}
                    className="text-xs text-kf-primary hover:underline flex items-center gap-1 flex-shrink-0 ml-3 disabled:opacity-70 disabled:cursor-wait"
                    title={downloadingIds.has(s.id) ? 'Henter og genererer signert PDF …' : 'Last ned signert PDF'}
                  >
                    {downloadingIds.has(s.id) ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" /> Henter …
                      </>
                    ) : (
                      <>
                        <Download className="h-3 w-3" /> PDF
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'grace' && (
            <div className="space-y-2">
              {gracePeriods.length === 0 && <div className="text-sm text-gray-500 py-8 text-center">Ingen grace-perioder</div>}
              {gracePeriods.map((g, i) => {
                const expires = new Date(g.grace_period_ends_at).getTime();
                const daysLeft = Math.round((expires - now) / (1000 * 60 * 60 * 24));
                const isResolved = !!g.resolved_at;
                const isExpired = !isResolved && expires < now;
                return (
                  <div key={i} className={`bg-white border rounded p-3 flex items-center justify-between text-sm ${
                    isResolved ? 'border-green-200 bg-green-50' :
                    isExpired ? 'border-red-200 bg-red-50' :
                    daysLeft < 7 ? 'border-yellow-200 bg-yellow-50' :
                    'border-gray-200'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {isResolved ? <CheckCircle2 className="h-4 w-4 text-green-600" /> :
                         isExpired ? <AlertTriangle className="h-4 w-4 text-red-600" /> :
                         <Clock className="h-4 w-4 text-yellow-600" />}
                        <span className="font-medium text-gray-900">{g.party_name || `${g.party_type} (ukjent)`}</span>
                        <span className="text-xs text-gray-500">{DPA_LABELS[g.dpa_type] || g.dpa_type}</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {isResolved ? (
                          <span className="text-green-700">Signert — grace-periode løst</span>
                        ) : isExpired ? (
                          <span className="text-red-700">Utløpt {Math.abs(daysLeft)} dager siden — bruker blokkeres</span>
                        ) : (
                          <span>Utløper om {daysLeft} dager ({new Date(g.grace_period_ends_at).toLocaleDateString('nb-NO')})</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'discrepancies' && (
            <div className="space-y-3">
              {discrepancies.length === 0 && <div className="text-sm text-gray-500 py-8 text-center">Ingen Brreg-avvik registrert</div>}
              {discrepancies.map(d => {
                const isResolved = !!d.resolved_at;
                const sevColor = d.severity === 'critical' ? 'red' : d.severity === 'warning' ? 'yellow' : 'blue';
                return (
                  <div key={d.id} className={`bg-white border rounded-lg p-4 ${
                    isResolved ? 'border-gray-200 opacity-60' :
                    sevColor === 'red' ? 'border-red-300' :
                    sevColor === 'yellow' ? 'border-yellow-300' :
                    'border-kf-blue-200'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            sevColor === 'red' ? 'bg-red-100 text-red-800' :
                            sevColor === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-kf-blue-100 text-kf-blue-800'
                          }`}>{d.severity}</span>
                          <span className="font-semibold text-gray-900">{d.party_name}</span>
                          <span className="text-xs text-gray-500">· {FIELD_LABELS[d.field_name] || d.field_name}</span>
                          <span className="text-xs text-gray-400">· {new Date(d.detected_at).toLocaleDateString('nb-NO')}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Lokalt lagret:</div>
                            <div className="bg-gray-50 px-3 py-2 rounded border border-gray-200 font-mono text-xs">{d.local_value || '(tom)'}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Fra Brønnøysund:</div>
                            <div className="bg-green-50 px-3 py-2 rounded border border-green-200 font-mono text-xs">{d.brreg_value}</div>
                          </div>
                        </div>
                        {isResolved && (
                          <div className="mt-3 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            <strong>Behandlet:</strong> {RESOLUTION_LABELS[d.resolution!]} av {d.resolved_by_name} · {new Date(d.resolved_at!).toLocaleString('nb-NO')}
                            {d.resolution_notes && <div className="mt-1 italic">Notat: {d.resolution_notes}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                    {!isResolved && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
                        <button onClick={() => resolveDiscrepancy(d.id, 'updated_from_brreg')}
                                className="px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200">
                          Oppdater fra Brreg
                        </button>
                        <button onClick={() => resolveDiscrepancy(d.id, 'kept_local', 'Brreg er utdatert')}
                                className="px-3 py-1 bg-kf-blue-100 text-kf-blue-800 rounded hover:bg-kf-blue-200">
                          Behold lokalt (Brreg utdatert)
                        </button>
                        <button onClick={() => {
                          const notes = prompt('Notat (valgfritt) — hva ble organisasjonen kontaktet om?');
                          if (notes !== null) resolveDiscrepancy(d.id, 'contacted_org', notes);
                        }}
                                className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200">
                          Kontaktet organisasjonen
                        </button>
                        <button onClick={() => resolveDiscrepancy(d.id, 'ignored')}
                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                          Ignorer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {publishTarget && (
        <PublishTemplateModal
          target={publishTarget}
          onClose={() => setPublishTarget(null)}
          onSuccess={() => { setPublishTarget(null); loadAll(); }}
        />
      )}

      {contentTarget && (
        <TemplateContentModal
          templateId={contentTarget.id}
          label={contentTarget.label}
          canEdit={contentTarget.canEdit}
          onClose={() => setContentTarget(null)}
          onSaved={() => { setContentTarget(null); loadAll(); }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// TemplateContentModal — vis / rediger malinnhold (systemadmin)
// ------------------------------------------------------------------
function TemplateContentModal({ templateId, label, canEdit, onClose, onSaved }: {
  templateId: string;
  label: string;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [isDraft, setIsDraft] = useState(true);
  const [templateName, setTemplateName] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch(`/api/dpa/admin/templates/${templateId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Kunne ikke hente avtaleinnhold');
        setContent(data.template.content || '');
        setNotes(data.template.notes || '');
        setIsDraft(!!data.template.is_draft_marker);
        setTemplateName(data.template.template_name || '');
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [templateId]);

  const save = async () => {
    if (content.length < 50) { setError('Innhold må være minst 50 tegn'); return; }
    setSaving(true); setError(null);
    try {
      const res = await authenticatedFetch(`/api/dpa/admin/templates/${templateId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, notes, is_draft_marker: isDraft })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lagring feilet');
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const requestClose = async () => {
    if (dirty && canEdit) {
      const ok = await confirm({
        title: 'Lukke uten å lagre?',
        message: 'Du har ulagrede endringer. Lukk vinduet uten å beholde dem?',
        variant: 'warning',
        confirmLabel: 'Forkast endringer',
        cancelLabel: 'Fortsett redigering',
      });
      if (!ok) return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-kf-primary flex items-center gap-2">
              {canEdit ? <Pencil className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              {canEdit ? 'Rediger utkast' : 'Vis innhold'} — {label}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{templateName}</p>
          </div>
          <button onClick={requestClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-kf-primary" /></div>
        ) : (
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />{error}
              </div>
            )}

            {canEdit ? (
              <>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-900">
                  <strong>Advarsel:</strong> Endringer i avtaleinnholdet påvirker hva motparter signerer.
                  Sørg for juridisk kvalitetssikring før du publiserer. Endringer logges i audit-loggen.
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 flex justify-between">
                    <span>Innhold (Markdown)</span>
                    <span className="text-gray-400">{content.length.toLocaleString('nb-NO')} tegn</span>
                  </label>
                  <textarea
                    value={content}
                    onChange={e => { setContent(e.target.value); setDirty(true); }}
                    rows={26}
                    className="mt-1 block w-full border border-gray-300 rounded px-3 py-2 font-mono text-xs leading-relaxed"
                    spellCheck={false}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Notater (kun synlig for systemadmin)</label>
                  <textarea
                    value={notes}
                    onChange={e => { setNotes(e.target.value); setDirty(true); }}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="F.eks. hva som er endret siden forrige versjon"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={isDraft} onChange={e => { setIsDraft(e.target.checked); setDirty(true); }} />
                  Marker som utkast (UTKAST-stempel synlig i signeringsflyt og PDF)
                </label>
              </>
            ) : (
              <>
                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                  {notes ? <em>Notat: {notes}</em> : <em className="text-gray-400">Ingen notater</em>} · {isDraft ? 'UTKAST' : 'Endelig versjon'}
                </div>
                <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed text-gray-800 bg-gray-50 border border-gray-200 rounded p-3 max-h-[65vh] overflow-auto">
                  {content}
                </pre>
              </>
            )}
          </div>
        )}

        <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
          <button onClick={requestClose} className="px-4 py-2 border border-gray-300 rounded text-sm">
            {canEdit ? 'Avbryt' : 'Lukk'}
          </button>
          {canEdit && (
            <button
              onClick={save}
              disabled={saving || content.length < 50}
              className="px-4 py-2 bg-kf-primary text-white rounded text-sm flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Lagre utkast
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// PublishTemplateModal — Vipps-verifisert mal-publisering (systemadmin)
// ------------------------------------------------------------------
function PublishTemplateModal({ target, onClose, onSuccess }: {
  target: { dpa_type: string; version: string; label: string };
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [step, setStep] = useState<'phone' | 'vipps_polling' | 'publishing' | 'done'>('phone');
  const [error, setError] = useState<string | null>(null);
  const [vippsMessage, setVippsMessage] = useState<string | null>(null);
  const [vippsVerified, setVippsVerified] = useState<{ full_name?: string; birth_date?: string } | null>(null);
  const pollingRef = useRef<number | null>(null);
  const abortedRef = useRef(false);

  useEffect(() => {
    abortedRef.current = false;
    return () => {
      abortedRef.current = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  const publishWithAuthReq = async (authReqId: string) => {
    setStep('publishing');
    try {
      const res = await authenticatedFetch('/api/dpa/admin/publish-template', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dpa_type: target.dpa_type, auth_req_id: authReqId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Publisering feilet');
      setStep('done');
      setTimeout(onSuccess, 1500);
    } catch (e: any) {
      setError(e.message);
      setStep('phone');
    }
  };

  const pollVipps = (authReqId: string, startInterval: number) => {
    let current = startInterval;
    const tick = async () => {
      if (abortedRef.current) return;
      try {
        const res = await authenticatedFetch(`/api/admin/vipps-2fa/status/${authReqId}`);
        const data = await res.json();
        if (abortedRef.current) return;
        if (data.status === 'pending') {
          if (data.slowDown) current = Math.min(current * 2, 60);
          pollingRef.current = window.setTimeout(tick, current * 1000);
          return;
        }
        pollingRef.current = null;
        if (data.status === 'complete' && data.purpose === 'dpa_signing') {
          setVippsVerified(data.display || null);
          publishWithAuthReq(authReqId);
        } else if (data.status === 'rejected') {
          setError('Du avviste Vipps-forespørselen');
          setStep('phone');
        } else if (data.status === 'expired') {
          setError('Vipps-forespørselen utløp — prøv igjen');
          setStep('phone');
        } else {
          setError(data.error || 'Vipps-verifisering feilet');
          setStep('phone');
        }
      } catch (e: any) {
        if (abortedRef.current) return;
        pollingRef.current = null;
        setError(e.message);
        setStep('phone');
      }
    };
    if (pollingRef.current) clearTimeout(pollingRef.current);
    pollingRef.current = window.setTimeout(tick, current * 1000);
  };

  const startVipps = async () => {
    setError(null);
    if (!/^\+?\d{8,15}$/.test(phoneNumber.replace(/\s/g, ''))) {
      setError('Ugyldig mobilnummer');
      return;
    }
    setStep('vipps_polling');
    try {
      const res = await authenticatedFetch('/api/admin/vipps-2fa/initiate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber, purpose: 'dpa_signing' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke starte Vipps');
      setVippsMessage(data.message);
      pollVipps(data.auth_req_id, data.poll_interval || 5);
    } catch (e: any) {
      setError(e.message);
      setStep('phone');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-kf-primary flex items-center gap-2">
              <Upload className="h-5 w-5" /> Publiser DPA-avtale
            </h2>
            <p className="text-sm text-gray-600 mt-1">{target.label} — v{target.version}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-3 text-xs text-kf-blue-900">
          <strong>Handlingen dokumenteres:</strong> Publisering er bindende for Kompetanseforbundet som databehandler.
          Vipps-verifisert person, tidspunkt og IP lagres som bevis for Datatilsynet.
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
            <AlertTriangle className="h-4 w-4 inline mr-2" />{error}
          </div>
        )}

        {step === 'phone' && (
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Ditt mobilnummer (Vipps)</span>
              <input
                type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)}
                placeholder="+47 XXX XX XXX"
                className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-sm">Avbryt</button>
              <button onClick={startVipps} className="px-4 py-2 bg-kf-primary text-white rounded text-sm flex items-center gap-2">
                <Smartphone className="h-4 w-4" /> Start Vipps-verifisering
              </button>
            </div>
          </div>
        )}

        {step === 'vipps_polling' && (
          <div className="text-center py-6 space-y-2">
            <Loader2 className="h-8 w-8 animate-spin text-kf-primary mx-auto" />
            <p className="text-sm text-gray-700">Åpne Vipps-appen på mobilen og bekreft</p>
            {vippsMessage && <p className="text-xs text-gray-500">{vippsMessage}</p>}
          </div>
        )}

        {step === 'publishing' && (
          <div className="text-center py-6 space-y-2">
            <Loader2 className="h-8 w-8 animate-spin text-kf-primary mx-auto" />
            <p className="text-sm text-gray-700">Publiserer på vegne av Kompetanseforbundet…</p>
            {vippsVerified?.full_name && (
              <p className="text-xs text-gray-500">Verifisert: {vippsVerified.full_name}</p>
            )}
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6 space-y-2">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <p className="text-sm text-green-800 font-medium">Avtalen er publisert</p>
            <p className="text-xs text-gray-600">Motparter kan nå signere.</p>
          </div>
        )}
      </div>
    </div>
  );
}
